import { Address, CompanySpecificService, Contract, ContractCompanySpecificService, ContractLight } from "@/types";
import { CreateOrderFromContractValues } from "@/components/organisms/CreateOrderFromContract/index";
import ContractTradeTypeEnum from "@/constants/enums/ContractTradeTypeEnum";
import { findDeliveryAddressObject } from "@/utils/AddressHelper";
import { ApiError } from "@/utils/apiErrors";

export const createOrderFromContractPlaceholder = (
  companyId?: number,
  userId?: number,
  contract?: ContractLight
): Partial<CreateOrderFromContractValues> | null => {
  if (!companyId || !userId || !contract) return null;
  return {
    companyId: companyId,
    userId: userId,
    amount: 0,
    unit: contract.contractProduct?.priceUnit.id ?? 1,
    orderAddress: null,
    contractId: contract.id,
    boxtypeId: 1,
    orderCurrency: contract.contractProduct?.currencyType,
    orderNeedsApproval: !contract.automaticSalesApproval,
    servicePriceOverrides: {},
    estimatedDelivery: new Date(),
    created: new Date(),
    batchNumber: "",
    latitude: "",
    longitude: "",
    capturedAt: null,
  };
};

export const getMappedServicesWithContractPricing = (
  companySpecificServicesData: CompanySpecificService[] | undefined,
  fullContract: Contract
): ContractCompanySpecificService[] => {
  if (!companySpecificServicesData || !fullContract?.companySpecificServices) return [];

  return companySpecificServicesData
    .filter(
      (service) =>
        service.priceUnit.code === "PPO" &&
        fullContract.companySpecificServices.some((s) => s.companySpecificServiceId === service.id)
    )
    .map((service) => {
      const contractService = fullContract.companySpecificServices.find(
        (s) => s.companySpecificServiceId === service.id
      );

      return {
        companySpecificServiceId: service.id,
        price: contractService?.price || service.price,
        currencyType: service.currency,
      };
    });
};

/** Swaps in the prices the user edited in the wizard, keyed by companySpecificServiceId. */
export const applyServicePriceOverrides = (
  services: ContractCompanySpecificService[],
  overrides: Record<number, number>
): ContractCompanySpecificService[] =>
  services.map((service) =>
    service.companySpecificServiceId in overrides
      ? { ...service, price: overrides[service.companySpecificServiceId] }
      : service
  );

export const determineIfEstimatedDeliveryIsRequired = (orderPlanning: boolean, automaticProccessing: boolean) => {
  return orderPlanning || automaticProccessing;
};

/**
 * The addresses an order uses unless the user picks another one. Prefers the address that was
 * selected when the contract was created; falls back to the company's current delivery address
 * only if the contract has none set.
 */
export const getContractDeliveryAddresses = (
  contract: Contract,
  tradeType: ContractTradeTypeEnum
): { ownAddress: Address | undefined; counterpartAddress: Address | undefined } => {
  const deliverFrom = contract.transportFromAddress?.address
    ? contract.transportFromAddress
    : findDeliveryAddressObject(contract.contractSellers[0]?.companyAddress ?? []);
  const deliverTo = contract.transportToAddress?.address
    ? contract.transportToAddress
    : findDeliveryAddressObject(contract.contractBuyers[0]?.companyAddress ?? []);

  return tradeType === ContractTradeTypeEnum.BUY
    ? { ownAddress: deliverTo, counterpartAddress: deliverFrom }
    : { ownAddress: deliverFrom, counterpartAddress: deliverTo };
};

/**
 * Maps the counterpart's chosen shipping address to the BE field for their side: on a purchase
 * order they are the seller (Deliver from), on a sales order the buyer (Deliver to).
 */
export const getTransportAddressIds = (tradeType: ContractTradeTypeEnum, counterpartAddressId: number | undefined) => ({
  transportFromAddressId: tradeType === ContractTradeTypeEnum.BUY ? (counterpartAddressId ?? null) : null,
  transportToAddressId: tradeType === ContractTradeTypeEnum.SELL ? (counterpartAddressId ?? null) : null,
});

export type OrderCreationErrorReason =
  | "slotSelectionRequired"
  | "slotNotAllowed"
  | "estimatedDeliveryInvalid"
  | "slotUnavailable"
  | "slotNotFound"
  | "slotFull"
  | "slotFractionMismatch"
  | "generic";

/** Backend `errorValues.reason` values that name the failure on their own, whatever the field. */
const SLOT_REASONS: Record<string, OrderCreationErrorReason> = {
  FULL: "slotFull",
  UNAVAILABLE: "slotUnavailable",
  NOT_FOUND: "slotNotFound",
  FRACTION_MISMATCH: "slotFractionMismatch",
};

/** Failures where the slot list the user picked from is stale, so it should be fetched again. */
const STALE_SLOT_REASONS: OrderCreationErrorReason[] = [
  "slotUnavailable",
  "slotNotFound",
  "slotFull",
  "slotFractionMismatch",
];

export const shouldRefreshReceptionSlots = (reason: OrderCreationErrorReason): boolean =>
  STALE_SLOT_REASONS.includes(reason);

/** The part of an `ApiError` that tells order-creation failures apart. */
type OrderCreationApiError = Pick<ApiError, "status"> & Partial<Pick<ApiError, "field" | "reason">>;

/**
 * Maps a slot-reservation/order-creation failure to an actionable UI reason. The backend's
 * `errorValues.reason` (REQUIRED, NOT_ALLOWED, FULL, UNAVAILABLE, NOT_FOUND, FRACTION_MISMATCH) and
 * `errorValues.field` are preferred. Without them we fall back to the HTTP status alone, where a 400
 * covers two distinct rejections that are told apart by whether a slot was submitted.
 */
export const mapOrderCreationError = (
  { status, field, reason }: OrderCreationApiError,
  receptionSlotId: number | null
): OrderCreationErrorReason => {
  if (reason && reason in SLOT_REASONS) return SLOT_REASONS[reason];

  if (reason === "REQUIRED" || reason === "NOT_ALLOWED") {
    if (field === "receptionSlotId") return reason === "REQUIRED" ? "slotSelectionRequired" : "slotNotAllowed";
    if (field === "estimatedDelivery") return "estimatedDeliveryInvalid";
  }

  switch (status) {
    case 400:
      return receptionSlotId ? "estimatedDeliveryInvalid" : "slotSelectionRequired";
    case 404:
      return "slotUnavailable";
    case 409:
      return "slotFull";
    case 422:
      return "slotFractionMismatch";
    default:
      return "generic";
  }
};
